import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Download, FileText, FolderOpen, Images, LogOut, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur, QUOTE_STATUS, INVOICE_STATUS } from "@/components/accounting/docUtils";
import { docTypeLabel } from "@/components/graphisme/Documents";
import { CMPortal } from "@/components/graphisme/CMPortal";
import { PilotageAccueil } from "@/components/graphisme/PilotageAccueil";
import { DriveExplorer } from "@/components/graphisme/Drive";
import { MonCompte } from "@/components/graphisme/MonCompte";
import { ElementUploader } from "@/pages/GraphismeEspaceDevis";
import { GELEMENT_STATUS, fmtD, clientStatusOf } from "@/components/graphisme/gUtils";
import { webStageLabel } from "@/lib/constants";

function WebProjectRow({ w, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="w-full text-left bg-white border border-neutral-200 rounded-xl p-4 space-y-1.5 hover:border-neutral-400 transition-colors">
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{w.name}</p>
          <p className="text-xs text-neutral-400">Site web · {webStageLabel(w.stage)}</p>
        </div>
        {w.elements_manquants > 0 && (
          <span className="text-[11px] rounded-full px-2.5 py-1 border shrink-0 bg-amber-50 text-amber-700 border-amber-200">
            {w.elements_manquants} élément(s) à fournir
          </span>
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <span>{w.progress}% terminé</span>
        {w.delivery_date && <span>Livraison : {fmtD(w.delivery_date)}</span>}
      </div>
      <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
        <div className="h-full bg-neutral-900 rounded-full transition-all" style={{ width: `${w.progress}%` }} />
      </div>
    </button>
  );
}

const sel = "w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white";


function ProjectRow({ p, onClick, testId }) {
  const st = clientStatusOf(p.status);
  const done = p.creations_done ?? (p.creations || []).filter((c) => c.status === "validee" || c.status === "finalisee").length;
  const total = p.creations_total ?? ((p.creations || []).length || p.qty_creations || 1);
  return (
    <button onClick={onClick} data-testid={testId}
      className="w-full text-left bg-white border border-neutral-200 rounded-xl p-4 space-y-1.5 hover:border-neutral-400 transition-colors">
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{p.name}</p>
          <p className="text-xs text-neutral-400 font-mono">{p.number}</p>
        </div>
        <span className={`text-[11px] rounded-full px-2.5 py-1 border shrink-0 ${st.cls}`}>{st.label}</span>
      </div>
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <span>{done} / {total} créations validées</span>
        {p.deadline && <span>Échéance : {fmtD(p.deadline)}</span>}
      </div>
      <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
        <div className="h-full bg-neutral-900 rounded-full transition-all" style={{ width: `${Math.round(100 * done / Math.max(total, 1))}%` }} />
      </div>
    </button>
  );
}

export default function GraphismeEspace() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [devis, setDevis] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "accueil");
  const [demandeOpen, setDemandeOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const loadDocs = () => api.get("/graphisme/espace/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { loadDocs(); }, []);
  useEffect(() => { if (tab === "documents") loadDocs(); }, [tab]);
  // Synchronise l'onglet avec l'URL (ex. « Agir → » depuis l'accueil alors que la page est déjà ouverte)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab) setTab(t);
  }, [searchParams]);
  const [d, setD] = useState({ objet: "", description: "", type: "", formats: "", dimensions: "", deadline: "", infos: "", projet_id: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/graphisme/espace/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/graphisme/espace/devis").then((r) => setDevis(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/graphisme/auth/me").then((r) => { setMe(r.data); load(); })
      .catch(() => navigate("/connexion"));
  }, []);
  // Retour sur l'accueil : rafraîchir l'overview (bannière réseaux sociaux, actions, etc.)
  useEffect(() => { if (me && tab === "accueil") load(); }, [tab]);

  const logout = async () => {
    await api.post("/graphisme/auth/logout").catch(() => {});
    navigate("/connexion");
  };

  const sendDemande = async () => {
    if (!d.objet.trim()) { toast.error("Objet requis"); return; }
    setBusy(true);
    try {
      const payload = { ...d, types: d.type ? [d.type] : [] };
      if (!payload.projet_id) delete payload.projet_id;
      await api.post("/graphisme/espace/demandes", payload);
      toast.success("Votre demande a bien été transmise à votre graphiste.");
      setDemandeOpen(false);
      setD({ objet: "", description: "", type: "", formats: "", dimensions: "", deadline: "", infos: "", projet_id: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (!me || !data) {
    return <div className="min-h-screen bg-neutral-100 flex items-center justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  }

  const plats = me.platforms || ["graphisme", "web"];
  const projetsEnCours = plats.includes("graphisme") ? (data.projects || []).filter((p) => !["paye", "livre", "termine"].includes(p.status) && p.status !== "nouvelle_demande") : [];
  const demandes = plats.includes("graphisme") ? (data.demandes || []) : [];
  const finalises = plats.includes("graphisme") ? (data.finalises || []) : [];
  const elementsPending = plats.includes("graphisme") ? (data.elements_pending || []) : [];
  const nElements = elementsPending.length;
  const webProjects = data.web_projects || [];
  const webEnCours = plats.includes("web") ? webProjects.filter((w) => !w.finalise) : [];
  const webFinalises = webProjects.filter((w) => w.finalise);
  const webElementsMissing = webEnCours.flatMap((w) =>
    Array.from({ length: w.elements_manquants }, () => w)).filter((w, i, arr) => arr.indexOf(w) === i);
  // Factures émises non soldées — mises en avant sur l'accueil
  const facturesDu = (data.invoices || []).filter((i) => i.status !== "payee");
  // Menu dynamique : une entrée par plateforme autorisée
  const TABS = [
    { id: "accueil", label: "Accueil" },
    ...(plats.includes("cm") ? [{ id: "cm", label: "Community Management" }] : []),
    ...(plats.includes("graphisme") ? [
      { id: "projets", label: "Graphisme" },
      { id: "demandes", label: "Mes demandes" },
      { id: "finalises", label: "Projets finalisés" },
    ] : []),
    ...(plats.includes("graphisme") || plats.includes("web") ? [{ id: "elements", label: "Éléments à fournir" }] : []),
    ...(plats.includes("web") ? [{ id: "web", label: "Web Design" }] : []),
    { id: "documents", label: "Mes documents" },
    { id: "compta", label: "Comptabilité" },
    { id: "compte", label: "Mon compte" },
  ];

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="gclient-espace">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <img src="/logo-dga.png" alt="DGA" className="w-8 h-8 rounded-lg object-cover" />
            <div>
              <p className="font-heading font-bold text-sm">Digital & Graphic Agency</p>
              <p className="text-xs text-neutral-500">Bonjour {data.client.name} 👋</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(projetsEnCours.length + webEnCours.length) > 0 && (
              <select value="" onChange={(e) => { if (e.target.value) navigate(e.target.value); }}
                className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white max-w-40 sm:max-w-52"
                data-testid="gclient-project-selector" title="Naviguer entre mes projets">
                <option value="">Mes projets ▾</option>
                {projetsEnCours.map((p) => <option key={p.id} value={`/graphisme/espace/projet/${p.id}`}>{`Graphisme — ${p.name}`}</option>)}
                {webEnCours.map((w) => <option key={w.id} value={`/graphisme/espace/web/${w.id}`}>{`Site web — ${w.name}`}</option>)}
              </select>
            )}
            <Button size="sm" variant="outline" onClick={() => navigate("/graphisme/espace/pilotage")} className="gap-1.5" data-testid="gclient-pilotage">
              <Images className="w-4 h-4" /> Pilotage de contenu
            </Button>
            <Button size="sm" onClick={() => setDemandeOpen(true)} className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" data-testid="gclient-new-demande">
              <Plus className="w-4 h-4" /> Nouvelle demande
            </Button>
            <Button variant="outline" size="icon" onClick={logout} title="Se déconnecter" data-testid="gclient-logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-2 sm:hidden">
          <select value={tab} onChange={(e) => setTab(e.target.value)} data-testid="gclient-nav-mobile"
            className="w-full text-sm border border-neutral-200 rounded-lg px-3 py-2 bg-white">
            {TABS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <nav className="max-w-3xl mx-auto px-4 hidden sm:flex flex-wrap gap-1 pb-2" data-testid="gclient-nav">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`gclient-tab-${t.id}`}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${tab === t.id ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-900"}`}>
              {t.label}{t.id === "elements" && nElements > 0 ? ` (${nElements})` : ""}
              {t.id === "demandes" && demandes.length > 0 ? ` (${demandes.length})` : ""}
              {t.id === "compta" && facturesDu.length > 0 ? ` (${facturesDu.length})` : ""}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {tab === "accueil" && (
          <>
            {data.actions.length > 0 ? (
              <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-5 space-y-2" data-testid="gclient-actions">
                <p className="font-heading font-bold text-orange-900 flex items-center gap-2"><AlertCircle className="w-5 h-5" /> Actions requises</p>
                {data.actions.map((a) => (
                  <button key={a.kind} onClick={() => a.url && navigate(a.url)} disabled={!a.url}
                    className={`w-full flex items-center justify-between gap-2 text-sm text-orange-800 bg-white/60 border border-orange-200 rounded-lg px-3 py-2.5 text-left transition-colors ${a.url ? "hover:border-orange-400 cursor-pointer" : ""}`}
                    data-testid={`gclient-action-${a.kind}`}>
                    <span>{a.text}</span>
                    {a.url && <span className="text-xs font-semibold shrink-0 underline">Agir →</span>}
                  </button>
                ))}
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-sm text-green-800" data-testid="gclient-all-good">
                Rien à faire pour le moment — tout est en ordre. ✅
              </div>
            )}
            {(data.social_missing || 0) > 0 && (
              <div className="bg-red-50 border-2 border-red-400 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="gclient-social-alert">
                <div className="flex items-center gap-3 min-w-0">
                  <AlertCircle className="w-6 h-6 text-red-600 shrink-0" />
                  <div>
                    <p className="font-heading font-bold text-red-900 text-sm">Accès réseaux sociaux incomplets</p>
                    <p className="text-xs text-red-700 mt-0.5">{data.social_missing} réseau(x) inclus dans votre prestation Community Management reste(nt) à configurer.</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => navigate("/graphisme/espace?tab=cm&cm=reseaux")}
                  className="bg-red-600 hover:bg-red-700 text-white shrink-0" data-testid="gclient-social-alert-open">
                  Compléter mes accès
                </Button>
              </div>
            )}
            {facturesDu.length > 0 && (
              <section className="space-y-2" data-testid="gclient-invoices-due">
                <h2 className="font-heading font-bold text-lg">Factures en attente de règlement ({facturesDu.length})</h2>
                {facturesDu.map((inv) => (
                  <div key={inv.id} className="bg-white border border-orange-200 rounded-xl px-4 py-3.5 flex flex-wrap items-center justify-between gap-3" data-testid={`gclient-invoice-due-${inv.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Facture N° {inv.number}</p>
                      <p className="text-xs text-neutral-400">Émise le {fmtD(inv.issue_date)}</p>
                    </div>
                    <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
                      <span className="text-sm font-semibold text-orange-700" data-testid={`gclient-invoice-due-amount-${inv.id}`}>
                        {eur(Math.max(0, (inv.total_ttc || 0) - (inv.paid_total || 0)))}
                      </span>
                      <span className="text-[11px] rounded-full px-2 py-0.5 border bg-orange-50 text-orange-700 border-orange-200">
                        {inv.status === "partiellement_payee" ? "Reste à régler" : "À régler"}
                      </span>
                      {inv.pdf_url && (
                        <a href={inv.pdf_url} target="_blank" rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline" data-testid={`gclient-invoice-due-pdf-${inv.id}`}>
                          Voir ma facture
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </section>
            )}
            {docs.length > 0 && (
              <section className="space-y-2" data-testid="gclient-recent-docs">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading font-bold text-lg">Mes documents récents</h2>
                  <button onClick={() => setTab("documents")} className="text-xs text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="gclient-recent-docs-all">Tout voir →</button>
                </div>
                {docs.slice(0, 3).map((doc) => (
                  <div key={doc.id} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-3" data-testid={`gclient-recent-doc-${doc.id}`}>
                    <FileText className="w-4 h-4 text-neutral-300 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.title}</p>
                      <p className="text-[11px] text-neutral-400 truncate">{docTypeLabel(doc.doc_type)} · transmis le {fmtD(doc.sent_at)}</p>
                    </div>
                    {doc.external_url ? (
                      <a href={doc.external_url} target="_blank" rel="noreferrer" className="text-xs bg-neutral-900 text-white rounded-lg px-3 py-1.5 hover:bg-neutral-800 shrink-0" data-testid={`gclient-recent-doc-link-${doc.id}`}>Ouvrir le lien</a>
                    ) : (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/graphisme/espace/documents/${doc.id}/file?dl=1`} className="text-xs bg-neutral-900 text-white rounded-lg px-3 py-1.5 hover:bg-neutral-800 shrink-0" data-testid={`gclient-recent-doc-dl-${doc.id}`}>Télécharger</a>
                    )}
                  </div>
                ))}
              </section>
            )}
            {(data.validations || []).length > 0 && (
              <section className="space-y-2" data-testid="gclient-validations">
                <h2 className="font-heading font-bold text-lg">Créations à valider</h2>
                {(data.validations || []).map((v) => {
                  const creaRow = (cr) => (
                    <button key={cr.id} onClick={() => navigate(`/graphisme/espace/projet/${v.project_id}?creation=${cr.id}`)}
                      data-testid={`gclient-validation-${cr.id}`}
                      className="w-full flex items-center justify-between gap-2 text-sm bg-orange-50/60 border border-orange-200 rounded-lg px-3 py-2.5 text-left hover:border-orange-400 transition-colors">
                      <span className="truncate">{cr.name}</span>
                      <span className="text-xs font-semibold text-orange-700 shrink-0">Valider →</span>
                    </button>
                  );
                  const folderName = (fid) => (v.folders || []).find((f) => f.id === fid)?.name;
                  const grouped = {};
                  v.creations.forEach((cr) => { const k = cr.folder_id || ""; (grouped[k] = grouped[k] || []).push(cr); });
                  return (
                    <div key={v.project_id} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2" data-testid={`gclient-validation-project-${v.project_id}`}>
                      <p className="text-sm font-semibold">{v.project_name}</p>
                      {Object.entries(grouped).map(([fid, crs]) => (
                        <div key={fid || "root"}>
                          {fid && folderName(fid) && (
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 flex items-center gap-1.5 mb-1">
                              <FolderOpen className="w-3.5 h-3.5" /> {folderName(fid)}
                            </p>
                          )}
                          <div className="space-y-1.5">{crs.map(creaRow)}</div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </section>
            )}
            {plats.includes("cm") && (
              <PilotageAccueil onOpenFull={() => navigate("/graphisme/espace/pilotage")} />
            )}
            {(data.new_documents || 0) > (data.docs_banner_ack || 0) && (
              <div className="bg-white border border-orange-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 relative" data-testid="gclient-new-docs">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-orange-50 border border-orange-200 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-orange-600" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{data.new_documents} nouveau(x) document(s)</p>
                    <p className="text-xs text-neutral-500">Votre graphiste vient de mettre un document à votre disposition.</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => setTab("documents")} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="gclient-new-docs-open">Consulter le document</Button>
                <button aria-label="Masquer la notification" title="Marquer comme vu"
                  onClick={async () => {
                    try { await api.post("/graphisme/espace/documents/ack-banner"); } catch { /* non bloquant */ }
                    setData((d) => ({ ...d, docs_banner_ack: d.new_documents || 0 }));
                  }}
                  className="absolute top-2 right-2 p-1 rounded-md text-orange-300 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                  data-testid="gclient-new-docs-dismiss"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {devis.length > 0 && (
              <section className="space-y-2" data-testid="gclient-devis-attente">
                <h2 className="font-heading font-bold text-lg">Devis en attente de validation ({devis.length})</h2>
                {devis.map((q2) => (
                  <div key={q2.id} className="bg-white border border-neutral-200 rounded-xl px-4 py-3.5 flex items-center justify-between gap-3" data-testid={`gclient-devis-row-${q2.id}`}>
                    <div>
                      <p className="text-sm font-medium">Devis {q2.number}</p>
                      <p className="text-xs text-neutral-400">{eur(q2.total_ttc)} — émis le {fmtD(q2.created_at)}</p>
                    </div>
                    <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white text-xs shrink-0 gap-1.5"
                      onClick={() => navigate(`/graphisme/espace/devis/${q2.id}`)} data-testid={`gclient-devis-view-${q2.id}`}>
                      <FileText className="w-3.5 h-3.5" /> Visualiser le devis
                    </Button>
                  </div>
                ))}
              </section>
            )}
            <section className="space-y-2">
              <h2 className="font-heading font-bold text-lg">Mes projets ({projetsEnCours.length + webEnCours.length})</h2>
              {webEnCours.map((w) => (
                <WebProjectRow key={w.id} w={w} testId={`gclient-web-project-${w.id}`} onClick={() => navigate(`/graphisme/espace/web/${w.id}`)} />
              ))}
              {projetsEnCours.slice(0, 3).map((p) => (
                <ProjectRow key={p.id} p={p} testId={`gclient-project-${p.id}`} onClick={() => navigate(`/graphisme/espace/projet/${p.id}`)} />
              ))}
              {projetsEnCours.length === 0 && webEnCours.length === 0 && (
                <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">
                  Aucun projet en cours. Créez votre première demande avec le bouton « Nouvelle demande ».
                </p>
              )}
            </section>
          </>
        )}

        {tab === "cm" && plats.includes("cm") && (
          <section className="space-y-2" data-testid="gclient-cm">
            <h2 className="font-heading font-bold text-lg">Community Management — publications</h2>
            <CMPortal />
          </section>
        )}

        {tab === "web" && plats.includes("web") && (
          <section className="space-y-2" data-testid="gclient-web">
            <h2 className="font-heading font-bold text-lg">Web Design — mes projets</h2>
            {[...webEnCours, ...webFinalises].map((w) => (
              <WebProjectRow key={w.id} w={w} testId={`gclient-web-project-${w.id}`} onClick={() => navigate(`/graphisme/espace/web/${w.id}`)} />
            ))}
            {webEnCours.length + webFinalises.length === 0 && <p className="text-sm text-neutral-400">Aucun projet web pour le moment.</p>}
          </section>
        )}

        {tab === "compta" && (
          <div className="space-y-6" data-testid="gclient-compta">
            <section className="space-y-2">
              <h2 className="font-heading font-bold text-lg">Mes devis ({(data.quotes || []).length})</h2>
              {(data.quotes || []).length === 0 && (
                <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">Aucun devis pour le moment.</p>
              )}
              {(data.quotes || []).map((q) => (
                <button key={q.id} onClick={() => navigate(`/graphisme/espace/devis/${q.id}`)}
                  className="w-full flex justify-between items-center gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3 hover:border-neutral-400 transition-colors text-left"
                  data-testid={`gclient-compta-devis-${q.id}`}>
                  <span className="text-sm font-medium flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-neutral-300 shrink-0" /><span className="truncate">Devis N° {q.number}</span></span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className={`text-[11px] rounded-full px-2 py-0.5 border ${(QUOTE_STATUS[q.status] || {}).cls || "bg-neutral-100 text-neutral-500 border-neutral-200"}`}>{(QUOTE_STATUS[q.status] || {}).label || q.status}</span>
                    <span className="text-sm font-semibold">{eur(q.total_ttc)}</span>
                  </span>
                </button>
              ))}
            </section>
            <section className="space-y-2">
              <h2 className="font-heading font-bold text-lg">Mes factures ({(data.invoices || []).length})</h2>
              {(data.invoices || []).length === 0 && (
                <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">Aucune facture pour le moment.</p>
              )}
              {(data.invoices || []).map((inv) => (
                <div key={inv.id} className="w-full flex flex-wrap justify-between items-center gap-2 bg-white border border-neutral-200 rounded-xl px-4 py-3" data-testid={`gclient-compta-facture-${inv.id}`}>
                  <span className="text-sm font-medium flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-neutral-300 shrink-0" />
                    <span className="truncate">Facture N° {inv.number}</span>
                    {inv.issue_date && <span className="text-[11px] text-neutral-400">· {fmtD(inv.issue_date)}</span>}
                  </span>
                  <span className="flex items-center gap-2 shrink-0 flex-wrap">
                    <span className={`text-[11px] rounded-full px-2 py-0.5 border ${(INVOICE_STATUS[inv.status] || {}).cls || "bg-neutral-100 text-neutral-500 border-neutral-200"}`}>{(INVOICE_STATUS[inv.status] || {}).label || inv.status}</span>
                    <span className="text-sm font-semibold">{eur(inv.status === "partiellement_payee" ? Math.max(0, (inv.total_ttc || 0) - (inv.paid_total || 0)) : inv.total_ttc)}</span>
                    {inv.pdf_url && <a href={inv.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline" data-testid={`gclient-compta-facture-pdf-${inv.id}`}>Voir ma facture</a>}
                  </span>
                </div>
              ))}
            </section>
          </div>
        )}

        {tab === "compte" && <MonCompte me={me} />}

        {tab === "projets" && (
          <section className="space-y-2" data-testid="gclient-projects">
            <h2 className="font-heading font-bold text-lg">Projets en cours</h2>
            {webEnCours.map((w) => (
              <WebProjectRow key={w.id} w={w} testId={`gclient-web-project-${w.id}`} onClick={() => navigate(`/graphisme/espace/web/${w.id}`)} />
            ))}
            {projetsEnCours.map((p) => (
              <ProjectRow key={p.id} p={p} testId={`gclient-project-${p.id}`} onClick={() => navigate(`/graphisme/espace/projet/${p.id}`)} />
            ))}
            {projetsEnCours.length === 0 && webEnCours.length === 0 && <p className="text-sm text-neutral-400">Aucun projet en cours.</p>}
          </section>
        )}

        {tab === "elements" && (
          <section className="space-y-2" data-testid="gclient-elements">
            <h2 className="font-heading font-bold text-lg">Éléments à fournir</h2>
            {webEnCours.filter((w) => w.elements_manquants > 0).map((w) => (
              <button key={w.id} onClick={() => navigate(`/graphisme/espace/web/${w.id}`)} data-testid={`gclient-web-elements-${w.id}`}
                className="w-full text-left bg-white border border-neutral-200 rounded-xl p-4 hover:border-neutral-400 transition-colors">
                <p className="text-sm font-medium">Site web « {w.name} »</p>
                <p className="text-xs text-amber-700">{w.elements_manquants} élément(s) à transmettre — cliquez pour les envoyer</p>
              </button>
            ))}
            {elementsPending.length === 0 && webEnCours.every((w) => w.elements_manquants === 0) ? (
              <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">Aucun élément en attente — merci !</p>
            ) : (
              elementsPending.map((e) => (
                <div key={e.id} className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={`gclient-element-${e.id}`}>
                  <p className="text-sm font-medium">{e.label}</p>
                  <p className="text-xs text-neutral-400 mb-1">Projet : {e.project_name} — {(GELEMENT_STATUS[e.status] || {}).label}</p>
                  <ElementUploader element={e} onDone={load} />
                </div>
              ))
            )}
          </section>
        )}

        {tab === "demandes" && (
          <section className="space-y-2" data-testid="gclient-demandes">
            <h2 className="font-heading font-bold text-lg">Mes demandes</h2>
            {demandes.length === 0 ? (
              <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">Aucune demande en cours.</p>
            ) : (
              demandes.map((d2) => (
                <button key={d2.id} onClick={() => navigate(`/graphisme/espace/projet/${d2.id}`)} data-testid={`gclient-demande-${d2.id}`}
                  className="w-full text-left bg-white border border-neutral-200 rounded-xl p-4 hover:border-neutral-400 transition-colors">
                  <div className="flex justify-between items-center gap-2">
                    <p className="text-sm font-medium">{d2.name}</p>
                    <span className="text-[11px] rounded-full px-2.5 py-1 border bg-yellow-50 text-yellow-700 border-yellow-200">En attente d'analyse</span>
                  </div>
                  <p className="text-xs text-neutral-400 mt-1">Reçue le {fmtD(d2.created_at)}</p>
                </button>
              ))
            )}
          </section>
        )}

        {tab === "finalises" && (
          <section className="space-y-2" data-testid="gclient-finalises">
            <h2 className="font-heading font-bold text-lg">Projets finalisés</h2>
            {webFinalises.map((w) => (
              <button key={w.id} onClick={() => navigate(`/graphisme/espace/web/${w.id}`)} data-testid={`gclient-web-finalise-${w.id}`}
                className="w-full text-left bg-white border border-neutral-200 rounded-xl p-4 hover:border-neutral-400 transition-colors">
                <p className="text-sm font-medium">{w.name}</p>
                <p className="text-xs text-green-600">✓ Site en ligne</p>
              </button>
            ))}
            {finalises.length === 0 && webFinalises.length === 0 ? (
              <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">Aucun projet finalisé pour le moment.</p>
            ) : (
              finalises.map((p) => (
                <div key={p.id} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2.5" data-testid={`gclient-finalise-${p.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-xs text-green-600">✓ {p.status === "paye" ? "Payé" : p.status === "livre" ? "Livré" : p.status === "termine" ? "Terminé" : "Fichiers disponibles"}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`gclient-finalise-open-${p.id}`}
                        onClick={() => navigate(`/graphisme/espace/projet/${p.id}`)}>Voir le projet</Button>
                      {(p.final_files || []).some((ff) => ff.files.some((f) => !f.external)) && (
                        <a href={`/api/graphisme/espace/projets/${p.id}/download-all`} data-testid={`gclient-finalise-dl-${p.id}`}
                          className="inline-flex items-center gap-1 h-8 px-3 text-xs border border-neutral-200 rounded-md hover:border-neutral-400 transition-colors">
                          <Download className="w-3.5 h-3.5" /> Tout (.ZIP)
                        </a>
                      )}
                    </div>
                  </div>
                  {(p.final_files || []).length === 0 && (
                    <p className="text-xs text-neutral-400 pt-2 border-t border-neutral-100" data-testid={`gclient-finalise-empty-${p.id}`}>
                      Aucun fichier final livré pour le moment — votre graphiste n'a pas encore mis de fichier à disposition.
                    </p>
                  )}
                  {(p.final_files || []).length > 0 && (
                    <div className="space-y-1.5 pt-2 border-t border-neutral-100" data-testid={`gclient-finalise-files-${p.id}`}>
                      {p.final_files.map((ff) => ff.files.map((f) => (
                        <div key={f.id} className="flex items-center justify-between gap-2 text-xs">
                          <span className="truncate text-neutral-600">{ff.creation_name} — {f.name}</span>
                          {f.external && f.url ? (
                            <a href={f.url} target="_blank" rel="noreferrer" data-testid={`gclient-finalfile-dl-${f.id}`}
                              className="inline-flex items-center gap-1 text-green-700 hover:text-green-900 font-medium shrink-0">
                              <Download className="w-3.5 h-3.5" /> Ouvrir (lien externe)
                            </a>
                          ) : (
                            <a href={`/api/graphisme/espace/creations/${ff.creation_id}/download-final?file_id=${f.id}`}
                              data-testid={`gclient-finalfile-dl-${f.id}`}
                              className="inline-flex items-center gap-1 text-green-700 hover:text-green-900 font-medium shrink-0">
                              <Download className="w-3.5 h-3.5" /> Télécharger
                            </a>
                          )}
                        </div>
                      )))}
                    </div>
                  )}
                </div>
              ))
            )}
          </section>
        )}

        {tab === "documents" && (
          <section className="space-y-3" data-testid="gclient-drive">
            <h2 className="font-heading font-bold text-lg">Documents</h2>
            <p className="text-xs text-neutral-400 -mt-1.5">Espace de fichiers partagé avec votre agence — dossiers, images, vidéos, PDF et liens. Vos devis et factures sont dans la rubrique « Comptabilité ».</p>
            <DriveExplorer side="client" />
          </section>
        )}
      </main>

      <Dialog open={demandeOpen} onOpenChange={setDemandeOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gclient-demande-modal" aria-describedby={undefined}>
          <DialogTitle className="font-heading font-bold text-lg">Nouvelle demande</DialogTitle>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Cette demande concerne</Label>
              <select className={`${sel} mt-1`} value={d.projet_id} onChange={(e) => setD({ ...d, projet_id: e.target.value })} data-testid="demande-projet">
                <option value="">Un nouveau projet</option>
                {projetsEnCours.map((p) => <option key={p.id} value={p.id}>Projet existant : {p.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Objet *</Label>
              <Input className="mt-1" value={d.objet} onChange={(e) => setD({ ...d, objet: e.target.value })} placeholder="Ex : Nouvelle carte des soins" data-testid="demande-objet" /></div>
            <div><Label className="text-xs">Description</Label>
              <Textarea rows={3} className="mt-1" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} data-testid="demande-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Type de création</Label>
                <Input className="mt-1" value={d.type} onChange={(e) => setD({ ...d, type: e.target.value })} placeholder="Flyer, logo…" data-testid="demande-type" /></div>
              <div><Label className="text-xs">Deadline souhaitée</Label>
                <Input type="date" className="mt-1" value={d.deadline} onChange={(e) => setD({ ...d, deadline: e.target.value })} data-testid="demande-deadline" /></div>
              <div><Label className="text-xs">Format</Label>
                <Input className="mt-1" value={d.formats} onChange={(e) => setD({ ...d, formats: e.target.value })} placeholder="A5…" /></div>
              <div><Label className="text-xs">Dimensions</Label>
                <Input className="mt-1" value={d.dimensions} onChange={(e) => setD({ ...d, dimensions: e.target.value })} placeholder="210×297 mm…" /></div>
            </div>
            <div><Label className="text-xs">Commentaire / informations complémentaires</Label>
              <Textarea rows={2} className="mt-1" value={d.infos} onChange={(e) => setD({ ...d, infos: e.target.value })} data-testid="demande-infos" /></div>
            <Button onClick={sendDemande} disabled={busy} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="demande-send">
              {busy ? "Envoi…" : "Envoyer ma demande"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
